import React, { useEffect } from 'react';
import { Schema } from '../../../../../lib/common/custom-activity/eventTypes';
import { FetchProfileMetadataEntry } from '../../../../profile-refresh/app/restTypes';
import {
    comparisonTextChanged,
    done,
    cancel,
    selectedCdpFieldChanged,
    selectedCdpObjectChanged,
    selectedCdpObjectComparisonFieldChanged,
    selectedSchemaEntryChanged,
    selectedComparisonTypeChanged,
    updateStateFromActivity,
    DataSelectorState,
} from './dataSelectorSlice';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { DockedFooter } from '../../../../../lib/client/components/dockedFooter';
import { fetchProfileMetadata } from '../../../../../lib/client/features/dataCloud/profileSlice';

export function DataSelector() {
    const activity = useAppSelector((state) => state.journey.activity);
    const schema = useAppSelector((state) => state.journey.schema);
    const selectedSchemaEntry = useAppSelector(
        (state) => state.dataSelector.selectedSchemaEntry,
    );

    const metadata = useAppSelector((state) => state.dataCloud.metadata);
    const metadataStatus = useAppSelector((state) => state.dataCloud.metadataStatus);
    const dataSelectorState = useAppSelector((state) => state.dataSelector);
    const selectedObject = useAppSelector(
        (state) => state.dataSelector.selectedCdpObject,
    );
    const selectedField = useAppSelector(
        (state) => state.dataSelector.selectedCdpField,
    );
    const selectedFieldToCompare = useAppSelector(
        (state) => state.dataSelector.selectedCdpObjectComparisonField,
    );
    const selectedComparisonType = useAppSelector(
        (state) => state.dataSelector.selectedComparisonType,
    );
    const comparisonText = useAppSelector(
        (state) => state.dataSelector.comparisonText,
    );
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (metadataStatus === 'idle') {
            dispatch(fetchProfileMetadata());
        }
    });

    useEffect(() => {
        // If activity changes, we need to load our selection state from it.
        if (activity !== null) {
            dispatch(updateStateFromActivity(activity));
        }
    }, [activity]);

    if (activity === null || schema === null) {
        return (
            <div>Waiting for Journey Builder to initialize the activity.</div>
        );
    }

    if (metadata === null) {
        const text =
            metadataStatus === 'failed'
                ? 'Error loading metadata from CDP. Please refresh and try again.'
                : 'Loading...';
        return <div>{text}</div>;
    }

    const objectSelectOptions: JSX.Element[] = getCdpObjectOptions(
        metadata.metadata,
    );
    const fieldSelectOptions: JSX.Element[] =
        selectedObject === null
            ? []
            : getCdpFieldOptions(metadata.metadata, selectedObject);
    const schemaSelectOptions: JSX.Element[] = getJourneySchemaOptions(schema);
    const comparisonTypeOptions: JSX.Element[] = getComparisonTypeOptions();

    return (
        <div>
            <div className="selectionContainer">
                <div>Fetch data from CDP from the Data Object:</div>
                <select
                    className="slds-select"
                    value={selectedObject || 'null'}
                    onChange={(event) =>
                        dispatch(selectedCdpObjectChanged(event.target.value))
                    }
                >
                    {selectedObject === null && (
                        <option disabled={true} value="null">
                            {' '}
                            Select an option{' '}
                        </option>
                    )}
                    {objectSelectOptions}
                </select>
            </div>
            <div className="selectionContainer">
                <div className="dropdownDescription">Where the field</div>
                <select
                    className="slds-select"
                    value={selectedField || 'null'}
                    onChange={(event) =>
                        dispatch(selectedCdpFieldChanged(event.target.value))
                    }
                >
                    {selectedObject !== null && selectedField === null && (
                        <option disabled={true} value="null">
                            {' '}
                            Select an option{' '}
                        </option>
                    )}
                    {fieldSelectOptions}
                </select>
            </div>
            <div className="selectionContainer">
                <div className="dropdownDescription">
                    Matches the contact based on the Journey Data field
                </div>
                <select
                    className="slds-select"
                    value={selectedSchemaEntry || 'null'}
                    onChange={(event) =>
                        dispatch(selectedSchemaEntryChanged(event.target.value))
                    }
                >
                    {selectedSchemaEntry === null && (
                        <option disabled={true} value="null">
                            {' '}
                            Select an option{' '}
                        </option>
                    )}
                    {schemaSelectOptions}
                </select>
            </div>
            <br />
            <br />
            <div className="selectionContainer">
                <div className="dropdownDescription">
                    Compare the value of the CDP field:
                </div>
                <select
                    className="slds-select"
                    value={selectedFieldToCompare || 'null'}
                    onChange={(event) =>
                        dispatch(
                            selectedCdpObjectComparisonFieldChanged(
                                event.target.value,
                            ),
                        )
                    }
                >
                    {selectedObject !== null &&
                        selectedFieldToCompare === null && (
                            <option disabled={true} value="null">
                                {' '}
                                Select an option{' '}
                            </option>
                        )}
                    {fieldSelectOptions}
                </select>
            </div>
            <div className="selectionContainer">
                <select
                    className="slds-select"
                    value={selectedComparisonType || 'null'}
                    onChange={(event) =>
                        dispatch(
                            selectedComparisonTypeChanged(event.target.value),
                        )
                    }
                >
                    {selectedComparisonType === null && (
                        <option disabled={true} value="null">
                            {' '}
                            Select an option{' '}
                        </option>
                    )}
                    {comparisonTypeOptions}
                </select>
            </div>
            <div className="selectionContainer">
                {selectedComparisonType === 'Equals' && (
                    <input
                        className="slds-select"
                        defaultValue={comparisonText || ''}
                        onChange={(event) =>
                            dispatch(comparisonTextChanged(event.target.value))
                        }
                    />
                )}
            </div>
            <DockedFooter
                canSubmit={() => canSubmit(dataSelectorState)}
                cancel={() => dispatch(cancel())}
                done={() => dispatch(done(activity))}
            />
        </div>
    );
}

function canSubmit(state: DataSelectorState) {
    return (
        state.dirty &&
        state.selectedCdpObject !== null &&
        state.selectedCdpField !== null &&
        state.selectedSchemaEntry !== null &&
        state.selectedCdpObjectComparisonField !== null &&
        state.selectedComparisonType !== null &&
        (!RequiresComparisonText(state.selectedComparisonType) ||
            state.comparisonText !== null)
    );
}

function RequiresComparisonText(comparisonType: string) {
    return comparisonType === 'Equals';
}

function getComparisonTypeOptions(): JSX.Element[] {
    const comparisonTypes = ['Exists', 'Equals'];
    const options: JSX.Element[] = [];
    for (let comparisonType of comparisonTypes) {
        options.push(
            <option key={comparisonType} value={comparisonType}>
                {comparisonType}
            </option>,
        );
    }
    return options;
}

function getCdpObjectOptions(
    metadataObjects: FetchProfileMetadataEntry[],
): JSX.Element[] {
    const options: JSX.Element[] = [];
    for (let object of metadataObjects) {
        options.push(
            <option key={object.name} value={object.name}>
                {object.displayName}
            </option>,
        );
    }
    return options;
}

function getCdpFieldOptions(
    metadataObjects: FetchProfileMetadataEntry[],
    selectedObjectName: string,
): JSX.Element[] {
    const selectedObject = metadataObjects.find(
        (m) => m.name === selectedObjectName,
    );
    if (selectedObject === undefined) {
        return [];
    }

    const options: JSX.Element[] = [];
    for (let object of selectedObject.fields) {
        options.push(
            <option key={object.name} value={object.name}>
                {object.displayName}
            </option>,
        );
    }
    return options;
}

function getJourneySchemaOptions(schema: Schema): JSX.Element[] {
    const options: JSX.Element[] = [];
    for (let object of schema) {
        options.push(
            <option key={object.key} value={object.key}>
                {object.name}
            </option>,
        );
    }
    return options;
}
